import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  GeoJSON,
  Popup,
  Tooltip,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, Maximize, Download, ChevronRight } from 'lucide-react';
import { renderToString } from 'react-dom/server';
import { GiBusStop } from 'react-icons/gi';
import { BsShop } from 'react-icons/bs';
import { FaBusAlt } from 'react-icons/fa';
import { generateReport } from 'utils/pdfReport';
import {
  getCategoryDettaglio,
  getLineDettaglio,
  getFermataMarkerColor,
  getMapLegendItems,
  getPuntoVenditaMarkerColor,
  getSchematicLinePositions,
  isRecordExtraurban,
} from 'utils/ambito';
import { scoreAccent, scoreBadgeBg } from 'utils/scoreColors';
import { CITY_COORDS, MACRO_BUTTONS } from 'constants/map';
import {
  fetchCompanyFile,
  getCompanyForCity,
  getZoneFileForClient,
  getClientCities,
  resolveCityDataKey,
  hasPptxConfigured,
  checkPptxAvailable,
  downloadClientPptx,
} from 'services/companyFileReader';
import { LabelFermata, LabelPuntoVendita, LabelLinea } from './MapLabels';
import { MarkerInfoCard } from './MarkerInfoCard';
import styles from './MapDashboard.module.css';

// ─── Constants & Color Palettes ───────────────────────────────────────────────

const BRAND_VIOLET = '#8C74FF'; // Purple selection highlight color

const URBAN_PALETTE = [
  "#00BBF9", "#06D6A0", "#4ECDC4", "#0096C7", "#52B788", "#277DA1", "#3A86FF", "#4361EE", "#43AA8B"
];

const EXTRAURBAN_PALETTE = [
  "#10b981", "#F72585", "#7209B7", "#B7094C", "#E07A5F", "#F4A261", "#FB5607", "#FF6B6B", "#E63946"
];

function getLineColor(shortName, isExtraurban = false) {
  return isExtraurban ? EXTRAURBAN_PALETTE[0] : URBAN_PALETTE[0];
}

const scoreColor = (score) => {
  if (score >= 8) return '#A3E635';
  if (score >= 6.5) return '#fde047';
  if (score >= 5) return '#fb923c';
  return '#ef4444';
};

// ─── Map sub-components (must live inside MapContainer) ───────────────────────

/**
 * MapViewController — owns the map view for city / element selection and keeps the
 * open popup card in the middle of the map viewport.
 *
 * Both jobs live here because they compete: `setView` targets the marker, while the
 * popup card sits above it, so the pan correction must always run after the view change.
 * `focusKey` lets the same target be re-applied (e.g. clicking the same marker twice).
 */
function MapViewController({ center, zoom, focusKey, animate = false, viewEnabled = true }) {
  const map = useMap();
  const openPopupRef = useRef(null);
  const timersRef = useRef([]);
  const frameRef = useRef(0);
  const userMovedRef = useRef(false);

  const clearPending = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    window.cancelAnimationFrame(frameRef.current);
  }, []);

  const centerPopup = useCallback(() => {
    const el = openPopupRef.current?.getElement?.()
      || map.getPane('popupPane')?.querySelector('.leaflet-popup');
    if (!el) return;
    const mapRect = map.getContainer().getBoundingClientRect();
    const popRect = el.getBoundingClientRect();
    if (!mapRect.width || !mapRect.height || !popRect.width || !popRect.height) return;

    const dx = (popRect.left + popRect.width / 2) - (mapRect.left + mapRect.width / 2);
    // A card taller than the viewport is aligned to the top edge instead of centred
    const dy = popRect.height > mapRect.height - 24
      ? popRect.top - (mapRect.top + 12)
      : (popRect.top + popRect.height / 2) - (mapRect.top + mapRect.height / 2);

    if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
    map.panBy([dx, dy], { animate: false });
  }, [map]);

  // The card renders (and grows) over several frames, and the view change can land
  // after the first pass — keep correcting until the position stops moving.
  const settlePopup = useCallback(() => {
    clearPending();
    frameRef.current = window.requestAnimationFrame(centerPopup);
    timersRef.current = [60, 140, 260, 420, 650].map((ms) => window.setTimeout(centerPopup, ms));
  }, [centerPopup, clearPending]);

  // Popup tracking is mount-scoped so a re-focus never loses the open card
  useEffect(() => {
    const onPopupOpen = (e) => {
      openPopupRef.current = e.popup;
      userMovedRef.current = false;
      settlePopup();
    };
    const onPopupClose = (e) => {
      if (openPopupRef.current === e.popup) {
        openPopupRef.current = null;
        clearPending();
      }
    };
    // A manual drag means the user chose their own framing — stop correcting
    const onDragStart = () => {
      userMovedRef.current = true;
      clearPending();
    };
    // Catches any other view change (selection focus, invalidateSize, zoom) — the
    // correction is a no-op once the card is already centred, so it cannot loop.
    const onMoveEnd = () => {
      if (userMovedRef.current || !openPopupRef.current) return;
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(centerPopup);
    };

    map.on('popupopen', onPopupOpen);
    map.on('popupclose', onPopupClose);
    map.on('dragstart', onDragStart);
    map.on('moveend', onMoveEnd);
    return () => {
      map.off('popupopen', onPopupOpen);
      map.off('popupclose', onPopupClose);
      map.off('dragstart', onDragStart);
      map.off('moveend', onMoveEnd);
      clearPending();
    };
  }, [map, settlePopup, clearPending, centerPopup]);

  useEffect(() => {
    if (!viewEnabled || !center) return undefined;
    let cancelled = false;

    const applyView = () => {
      if (cancelled) return;
      map.invalidateSize({ animate: false });
      map.setView(center, zoom ?? map.getZoom(), animate ? { animate: true, duration: 0.5 } : { animate: false });
      if (openPopupRef.current) settlePopup();
    };

    applyView();

    // Embedded layouts settle late: re-apply only while the container size is still stale
    const retry = window.setTimeout(() => {
      if (cancelled) return;
      const size = map.getSize();
      const rect = map.getContainer().getBoundingClientRect();
      const stale = !size.x || !size.y
        || Math.abs(size.x - rect.width) > 2
        || Math.abs(size.y - rect.height) > 2;
      if (stale) applyView();
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(retry);
    };
  }, [focusKey, center, zoom, animate, viewEnabled, map, settlePopup]);

  return null;
}

/** Anchor of a line's label marker — the map focuses here so the popup matches the marker. */
function getLineLabelPosition(el) {
  if (!el?.path?.length) return null;
  if (el.hasGtfs === false || el.path.length <= 1) return el.path[0];
  const idSeed = el.id.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  const labelFrac = idSeed % 2 === 0
    ? 0.72 + (idSeed % 18) / 100
    : 0.08 + (idSeed % 18) / 100;
  return el.path[Math.min(Math.floor(el.path.length * labelFrac), el.path.length - 1)];
}

function MapFitBounds({ positions, zoom = 13, padFactor = 0.35, enabled = true }) {
  const map = useMap();
  const key = positions.map((p) => p.join(',')).join('|') + zoom + padFactor + enabled;
  const userInteractedRef = useRef(false);

  const applyFit = useCallback(() => {
    // Disabled in city mode: MapViewController owns the view there (selection must win)
    if (!enabled || !positions.length) return;
    map.invalidateSize({ animate: false });
    if (positions.length === 1) {
      map.setView(positions[0], zoom, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(positions.map(([lat, lng]) => L.latLng(lat, lng)));
    // maxZoom keeps multi-city (e.g. SETA) overview wide enough to see every city
    map.fitBounds(bounds.pad(padFactor), { animate: false, maxZoom: 10 });
  }, [map, positions, zoom, padFactor, enabled]);

  useEffect(() => {
    userInteractedRef.current = false;
  }, [key]);

  useEffect(() => {
    if (positions.length === 0) return undefined;

    const inIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();

    const markInteracted = () => {
      userInteractedRef.current = true;
    };
    map.on('dragstart', markInteracted);
    map.on('zoomstart', markInteracted);

    // Same-origin embed: read parent wrapper (opacity / pointer-events / z-index)
    const isHostVisible = () => {
      try {
        const frame = window.frameElement;
        if (!frame) return null;
        let el = frame.parentElement || frame;
        for (let i = 0; i < 5 && el; i += 1) {
          const style = el.ownerDocument.defaultView.getComputedStyle(el);
          if (style.display === 'none' || style.visibility === 'hidden') return false;
          if (parseFloat(style.opacity) < 0.05) return false;
          if (style.pointerEvents === 'none') return false;
          const z = style.zIndex;
          if (z !== 'auto' && Number(z) < 0) return false;
          el = el.parentElement;
        }
        return true;
      } catch {
        return null;
      }
    };

    const allCitiesInView = () => {
      if (positions.length < 2) return true;
      const size = map.getSize();
      if (!size.x || !size.y) return false;
      const view = map.getBounds();
      if (!view || !view.isValid()) return false;
      // Require every city roughly inside the padded view
      return positions.every(([lat, lng]) => view.pad(-0.02).contains(L.latLng(lat, lng)));
    };

    let lastHostVisible = isHostVisible();

    const sync = () => {
      map.invalidateSize({ animate: false });

      const hostVisible = isHostVisible();
      const becameVisible = hostVisible === true && lastHostVisible === false;
      if (hostVisible !== null) lastHostVisible = hostVisible;

      const size = map.getSize();
      const rect = map.getContainer().getBoundingClientRect();
      const sizeMismatch =
        rect.width > 0 &&
        rect.height > 0 &&
        (Math.abs(size.x - rect.width) > 2 || Math.abs(size.y - rect.height) > 2);

      const shouldFit =
        !userInteractedRef.current &&
        (becameVisible ||
          sizeMismatch ||
          size.x === 0 ||
          size.y === 0 ||
          (positions.length > 1 && !allCitiesInView()) ||
          // Cross-origin / unknown host: keep healing until markers fit
          (inIframe && hostVisible !== false && positions.length > 1 && !allCitiesInView()));

      if (shouldFit || becameVisible) applyFit();
    };

    sync();
    const delays = inIframe
      ? [50, 150, 400, 800, 1500, 3000, 5000, 8000]
      : [100, 400];
    const timeoutIds = delays.map((ms) => window.setTimeout(sync, ms));

    const onResize = () => sync();
    window.addEventListener('resize', onResize);
    window.addEventListener('focus', onResize);

    const onMessage = (event) => {
      const type = event?.data?.type;
      if (
        type === 'mystery-client:invalidate' ||
        type === 'mystery-client:show' ||
        type === 'mystery-client:resize'
      ) {
        userInteractedRef.current = false;
        window.setTimeout(sync, 50);
        window.setTimeout(sync, 250);
      }
    };
    window.addEventListener('message', onMessage);

    let ro;
    const container = map.getContainer()?.parentElement;
    if (typeof ResizeObserver !== 'undefined' && container) {
      ro = new ResizeObserver(() => sync());
      ro.observe(container);
    }

    // Parent keeps iframe mounted with opacity:0 — poll until Tutte cities are in view
    const pollMs = inIframe ? 500 : 0;
    const pollId = pollMs
      ? window.setInterval(() => {
        if (userInteractedRef.current) return;
        if (positions.length > 1 && allCitiesInView() && map.getSize().x > 0) {
          // Still invalidate once in case tiles were skipped while opacity:0
          const hostVisible = isHostVisible();
          if (hostVisible === true || hostVisible === null) {
            map.invalidateSize({ animate: false });
          }
          return;
        }
        sync();
      }, pollMs)
      : null;

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      if (pollId) window.clearInterval(pollId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('focus', onResize);
      window.removeEventListener('message', onMessage);
      ro?.disconnect();
      map.off('dragstart', markInteracted);
      map.off('zoomstart', markInteracted);
    };
  }, [key, map, positions, applyFit]);

  return null;
}

function getCityScore(cittaObj, cityName) {
  const dataKey = resolveCityDataKey(cittaObj, cityName);
  const cd = cittaObj?.[dataKey];
  if (!cd) return null;
  // City scores are normalized and inherited from each city JSON by useClientData.
  if (typeof cd.indice_sintetico === 'number') return cd.indice_sintetico;
  if (typeof cd['indice sintetico'] === 'number') return cd['indice sintetico'];
  return null;
}

function lookupCityCoords(cityName) {
  if (!cityName) return [45.4642, 9.19];
  const upper = cityName.toUpperCase().trim();
  if (CITY_COORDS[upper]) return CITY_COORDS[upper];
  if (CITY_COORDS[cityName]) return CITY_COORDS[cityName];
  // Fallback: strip accents/diacritics for matching
  const stripped = upper.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const match = Object.keys(CITY_COORDS).find((k) => k.toUpperCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === stripped);
  return match ? CITY_COORDS[match] : [45.4642, 9.19];
}

/**
 * MapPopupCloser — imperatively closes any open Leaflet popup
 * whenever `closeSignal` increments (e.g. sidebar interaction).
 */
function MapPopupCloser({ closeSignal }) {
  const map = useMap();
  useEffect(() => {
    if (closeSignal > 0) map.closePopup();
  }, [closeSignal, map]);
  return null;
}

function MapZoomControls() {
  const map = useMap();
  return (
    <div className="leaflet-bottom leaflet-right" style={{ pointerEvents: 'auto' }}>
      <div className="leaflet-control" style={{ marginRight: '1.5rem', marginBottom: '1.5rem' }}>
        <div className={styles.zoomControls}>
          <button className={styles.zoomBtn} onClick={() => map.setZoom(map.getZoom() + 1)} aria-label="Zoom in">+</button>
          <button className={styles.zoomBtn} onClick={() => map.setZoom(map.getZoom() - 1)} aria-label="Zoom out">−</button>
        </div>
      </div>
    </div>
  );
}

function MapLegend({ cityData, revealed, activeFilters, onToggleFilter, onShowAll, onHideAll }) {
  if (!revealed) return null;
  const items = getMapLegendItems(cityData);
  const allOn = items.every((it) => activeFilters.has(it.id));
  const allOff = items.every((it) => !activeFilters.has(it.id));

  const iconFor = (item) => {
    if (item.id.startsWith('linee')) {
      return (
        <div className={styles.legendIcon} style={{ background: item.urban ? URBAN_PALETTE[0] : EXTRAURBAN_PALETTE[0] }}>
          <FaBusAlt size={11} color="#fff" />
        </div>
      );
    }
    if (item.id.startsWith('fermate')) {
      return (
        <div
          className={styles.legendIcon}
          style={{
            background: item.urban ? '#FFF260' : '#D9E9AD',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}
        >
          <GiBusStop size={14} color={item.urban ? '#000' : '#1B1C1C'} />
        </div>
      );
    }
    return (
      <div className={styles.legendIcon} style={{ background: item.urban ? '#FF852B' : '#10b981' }}>
        <BsShop size={11} color="#fff" />
      </div>
    );
  };

  return (
    <div className="leaflet-bottom leaflet-right" style={{ pointerEvents: 'auto', zIndex: 1000 }}>
      <div className="leaflet-control" style={{ marginRight: '5.5rem', marginBottom: '1rem' }}>
        <div className={styles.mapLegend}>
          <div className={styles.legendHeader}>
            <span className={styles.legendTitle}>LEGENDA</span>
            <div className={styles.legendBulkBtns}>
              <button
                className={`${styles.legendBulkBtn} ${allOn ? styles.legendBulkBtnActive : ''}`}
                onClick={onShowAll}
                title="Mostra tutti"
              >
                Tutti
              </button>
              <button
                className={`${styles.legendBulkBtn} ${allOff ? styles.legendBulkBtnActive : ''}`}
                onClick={onHideAll}
                title="Nascondi tutti"
              >
                Nessuno
              </button>
            </div>
          </div>
          {items.map((item) => {
            const isOn = activeFilters.has(item.id);
            return (
              <div key={item.id} className={styles.legendItem}>
                {iconFor(item)}
                <span
                  className={styles.legendLabel}
                  style={{ color: isOn ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)' }}
                >
                  {item.label}
                </span>
                <button
                  className={`${styles.legendToggle} ${isOn ? styles.legendToggleOn : ''}`}
                  onClick={() => onToggleFilter(item.id)}
                  aria-label={isOn ? `Nascondi ${item.label}` : `Mostra ${item.label}`}
                  aria-pressed={isOn}
                >
                  <span
                    className={styles.legendToggleThumb}
                    style={{
                      left: isOn ? 'calc(100% - 0.15rem - 0.8rem)' : '0.15rem',
                      background: isOn ? '#1a2e00' : 'rgba(255,255,255,0.6)',
                    }}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * CityZoneLayer — loads and renders a city's boundary GeoJSON.
 * Shows a muted style when revealed=false (multi-city overview).
 */
function CityZoneLayer({ cityName, companyName, revealed, onZoneClick, onLoadingChange }) {
  const [geoData, setGeoData] = useState(null);

  useEffect(() => {
    setGeoData(null);
    if (onLoadingChange) onLoadingChange(true);

    const zoneFile = getZoneFileForClient(companyName, cityName);
    if (!zoneFile) {
      if (onLoadingChange) onLoadingChange(false);
      return undefined;
    }

    const owner = companyName || getCompanyForCity(cityName) || 'ATB';

    let isMounted = true;
    fetchCompanyFile(owner, zoneFile)
      .then((d) => {
        if (isMounted && d) {
          setGeoData(d);
        }
      })
      .catch(() => {
        // Zone GeoJSON is optional per city.
      })
      .finally(() => {
        if (isMounted && onLoadingChange) onLoadingChange(false);
      });

    return () => {
      isMounted = false;
    };
  }, [cityName, companyName, onLoadingChange]);

  if (!geoData) return null;
  return (
    <GeoJSON
      key={`zone-${cityName}-${revealed}`}
      data={geoData}
      style={(feature) => {
        const props = feature.properties || {};
        const hasCustomStyle = !!(
          props.stroke || props.color || props['stroke-color'] ||
          props.strokeColor || props.fill || props.fillColor || props['fill-color']
        );
        if (revealed) {
          return {
            color: props['stroke-color'] || props.strokeColor || props.stroke || props.color || '#A3E635',
            weight: props['stroke-width'] || props.strokeWidth || props.weight || 2,
            opacity: props['stroke-opacity'] || props.strokeOpacity || props.opacity || 0.7,
            fillColor: props['fill-color'] || props.fillColor || props.fill || '#A3E635',
            fillOpacity: props['fill-opacity'] !== undefined ? parseFloat(props['fill-opacity']) : (props.fillOpacity !== undefined ? parseFloat(props.fillOpacity) : (hasCustomStyle ? 0.3 : 0.06)),
            dashArray: props['dash-array'] || props.dashArray || (hasCustomStyle ? '' : '5 4'),
          };
        }
        // Pre-reveal: highlighted zone inviting a click
        return {
          color: '#A3E635',
          weight: 2.5,
          opacity: 0.9,
          fillColor: '#A3E635',
          fillOpacity: 0.08,
          dashArray: '6 5',
        };
      }}
      eventHandlers={{ click: onZoneClick }}
    />
  );
}

/**
 * CityInfoMarker — city name + indice sintetico (used on multi-city overview to pick a city).
 */
function CityInfoMarker({ center, cityName, score, onClick }) {
  const col = scoreAccent(score);
  const badgeBg = scoreBadgeBg(score);

  const scoreStr = score !== null && score !== undefined ? score.toFixed(2) : '—';
  const html = `
    <div onclick="" style="
      transform: translate(-50%, -50%);
      background: rgba(18, 23, 6, 0.93);
      border: 2px solid #A3E635;
      border-radius: 18px;
      padding: 18px 28px 16px;
      text-align: center;
      cursor: pointer;
      box-shadow: 0 6px 32px rgba(0,0,0,0.65), 0 0 0 1px rgba(163,230,53,0.18);
      font-family: Inter, sans-serif;
      min-width: 200px;
      pointer-events: auto;
    ">
      <div style="font-size:24px;font-weight:900;color:#FFFFFF;letter-spacing:.04em;margin-bottom:10px;">${cityName}</div>
      <div style="
        display:inline-block;padding:6px 14px;border-radius:12px;
        background:${badgeBg};border:1.5px solid ${col};
        font-size:32px;font-weight:900;color:#FFFFFF;line-height:1;font-variant-numeric:tabular-nums;
      ">${scoreStr}</div>
      <div style="font-size:10px;color:rgba(255,255,255,0.45);margin-top:6px;letter-spacing:.1em;">INDICE SINTETICO</div>
    </div>
  `;

  const icon = L.divIcon({
    html,
    className: '',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

  return (
    <Marker
      position={center}
      icon={icon}
      zIndexOffset={1000}
      eventHandlers={{ click: onClick }}
    />
  );
}

// Bottom panel components removed

// ─── Main component ────────────────────────────────────────────────────────────

/**
 * MapDashboard — fullscreen Leaflet map with all interactive overlays.
 *
 * @param {object}   props.company        - The currently selected Company object
 * @param {array}    props.gtfsLines      - GTFS GeoJSON features (optional)
 * @param {string}   props.activeMacro    - Currently active macro-category key
 * @param {string}   props.selectedCity   - Currently selected city name
 * @param {boolean}  props.compareMode    - Whether compare mode is active
 * @param {Function} props.onToggleCompare - Toggle compare mode
 */
const MapDashboard = ({
  company,
  gtfsLines = null,
  activeMacro,
  selectedCity,
  userCompany,
  onCitySelect,
  compareMode,
  onToggleCompare,
  onRevealChange,
}) => {
  const cittaObj = useMemo(
    () => company?.citta || company?.['città'] || {},
    [company]
  );

  const configuredCities = useMemo(() => {
    const fromConfig = getClientCities(userCompany);
    if (fromConfig.length > 0) return fromConfig;
    return Object.keys(cittaObj);
  }, [userCompany, cittaObj]);

  const isMultiCity = configuredCities.length > 1;
  const inCityMode = isMultiCity ? !!selectedCity : true;

  const activeCityKey = selectedCity
    ? resolveCityDataKey(cittaObj, selectedCity)
    : resolveCityDataKey(cittaObj, configuredCities[0]) || configuredCities[0] || 'BERGAMO';

  const cityName = activeCityKey;
  const zoneCityName = selectedCity || configuredCities[0];
  const cityData = cittaObj[cityName];
  // Coordinates come from the clients.json city name, not the data key — a data key that
  // doesn't match the JSON (e.g. SETA Piacenza) must not fall back to another city's centre.
  const center = useMemo(
    () => lookupCityCoords(selectedCity || cityName),
    [selectedCity, cityName]
  );

  const overviewPositions = useMemo(
    () => configuredCities.map((c) => lookupCityCoords(c)),
    [configuredCities]
  );

  const fitPositions = useMemo(() => (inCityMode ? [center] : overviewPositions), [inCityMode, center, overviewPositions]);

  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedMacroPanel, setSelectedMacroPanel] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [downloadSubmenuOpen, setDownloadSubmenuOpen] = useState(false);
  const [pptxAvailable, setPptxAvailable] = useState(false);
  const downloadMenuRef = useRef(null);
  // Signal used to imperatively close Leaflet popups from outside the map
  const [popupCloseSignal, setPopupCloseSignal] = useState(0);
  // Map content is shown immediately in city mode (no click-to-explore gate)
  const mapRevealed = inCityMode;
  const [isZoneLoading, setIsZoneLoading] = useState(false);
  // Legend filter — Set of legend item ids that are currently visible
  const [activeFilters, setActiveFilters] = useState(() => new Set(['linee_urban', 'linee_extra', 'fermate_urban', 'fermate_extra', 'pv_urban', 'pv_extra']));

  // Notify parent whenever reveal state changes
  useEffect(() => { onRevealChange?.(mapRevealed); }, [mapRevealed, onRevealChange]);

  useEffect(() => {
    if (!downloadMenuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
        setDownloadSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [downloadMenuOpen]);

  // Resolve whether the uploaded PPTX for this client/city is available
  useEffect(() => {
    let cancelled = false;
    const companyName = company?.azienda;
    const year = company?.anno;

    if (!companyName || !year || !hasPptxConfigured(companyName, selectedCity)) {
      setPptxAvailable(false);
      return undefined;
    }

    setPptxAvailable(false);
    checkPptxAvailable(companyName, selectedCity, year)
      .then((ok) => {
        if (!cancelled) setPptxAvailable(ok);
      })
      .catch(() => {
        if (!cancelled) setPptxAvailable(false);
      });

    return () => { cancelled = true; };
  }, [company?.azienda, company?.anno, selectedCity]);

  const searchRef = useRef(null);

  // Where a clicked element pulled the view. Tagged with the city it belongs to so
  // switching city never renders the map with the previous city's centre.
  const [elementFocus, setElementFocus] = useState(null);
  const activeFocus = elementFocus?.city === (selectedCity || '') ? elementFocus : null;
  const viewCenter = activeFocus?.center || center;
  const viewZoom = activeFocus?.zoom ?? (inCityMode ? 10 : 11);
  // Re-applies the view when the same target is picked twice
  const viewKey = `${selectedCity || 'all'}|${activeFocus?.key ?? 'city'}`;

  // Sync with city change — reset view for the new city
  useEffect(() => {
    setElementFocus(null);
    setSelectedElement(null);
    // Reset filters to show-all for the new city
    setActiveFilters(new Set(['linee_urban', 'linee_extra', 'fermate_urban', 'fermate_extra', 'pv_urban', 'pv_extra']));
  }, [center, inCityMode, selectedCity]);

  // Reset popups when macro/compare changes
  useEffect(() => {
    setSelectedMacroPanel(null);
    setPopupCloseSignal((s) => s + 1);
  }, [activeMacro, compareMode, inCityMode]);

  // ── Map elements ──────────────────────────────────────────────────────────
  const mapElements = useMemo(() => {
    const elements = [];
    const [lat, lng] = center;

    // Lines — real GTFS geometry when available, schematic fallback
    const detailLinee = getLineDettaglio(cityData);
    const normLineKey = (s) => String(s || '').toLowerCase().replace(/linea\s+/g, '').replace(/\s+/g, '').replace(/-/g, '');
    const findLineDetail = (routeId, shortName) => {
      if (detailLinee[shortName]) return detailLinee[shortName];
      if (detailLinee[routeId]) return detailLinee[routeId];
      const nShort = normLineKey(shortName);
      const nId = normLineKey(routeId);
      for (const [k, v] of Object.entries(detailLinee)) {
        const nk = normLineKey(k);
        if (nk === nShort || nk === nId) return v;
      }
      return null;
    };

    if (gtfsLines && gtfsLines.length > 0) {
      // A route can be split across features and can use MultiLineString geometry.
      // Merge every segment into one map element without flattening it for rendering.
      const routeMap = new Map();
      gtfsLines.forEach((feat) => {
        const props = feat.properties || {};
        const { route_id, route_short_name, route_long_name } = props;
        const displayName = route_short_name || route_id || route_long_name || 'Sconosciuta';
        const uKey = normLineKey(displayName);

        const geom = feat.geometry;
        let coordSets = [];
        if (geom?.type === 'MultiLineString') coordSets = geom.coordinates;
        else if (geom?.type === 'LineString') coordSets = [geom.coordinates];
        else return;

        if (!routeMap.has(uKey)) {
          routeMap.set(uKey, {
            displayName,
            lineDetail: findLineDetail(route_id, displayName) || {},
            allCoords: [],
          });
        }

        const entry = routeMap.get(uKey);
        coordSets.forEach((coordSet) => {
          const segment = coordSet.map(([lngC, latC]) => [latC, lngC]);
          if (segment.length > 0) entry.allCoords.push(segment);
        });
      });

      routeMap.forEach(({ displayName, lineDetail, allCoords }, uKey) => {
        if (allCoords.length === 0) return;
        if (!lineDetail || Object.keys(lineDetail).length === 0) return;

        const path = allCoords.flat();
        elements.push({
          id: `linea-${uKey}`,
          type: 'linea',
          label: `Linea ${displayName}`,
          path,
          multiPath: allCoords,
          score: lineDetail['punteggio linea'] ?? 0,
          // Always use legend palette color — ignore raw GTFS color property
          routeColor: getLineColor(displayName, lineDetail.isExtraurban),
          details: lineDetail,
        });
      });
    } else {
      const lineNames = cityData?.linee_urbane?.linee ?? Object.keys(detailLinee);
      const positions = getSchematicLinePositions(center, lineNames.length, cityName);
      lineNames.forEach((name, idx) => {
        const pos = positions[idx] || center;
        const detail = detailLinee[name] || {};

        elements.push({
          id: `linea-${name}`,
          type: 'linea',
          label: `Linea ${name}`,
          path: [pos],
          hasGtfs: false,
          score: detail['punteggio linea'] ?? (6 + ((idx * 1.7) % 4)),
          details: detail,
          routeColor: getLineColor(name, detail.isExtraurban),
        });
      });
    }

    // Stops
    const detailFermate = getCategoryDettaglio(cityData, 'fermate');
    const fermataKeys = Object.keys(detailFermate);
    if (fermataKeys.length > 0) {
      const seenFermatePos = new Set();
      fermataKeys.forEach(k => {
        const f = detailFermate[k];
        let pos = null;
        if (f['posizione su mappa']) {
          const [pLat, pLng] = f['posizione su mappa'].split(',');
          if (pLat && pLng) pos = [parseFloat(pLat), parseFloat(pLng)];
        }
        if (pos) {
          // Deduplicate by coordinates to avoid same stop appearing from both urban and extraurban sources
          const posKey = `${pos[0].toFixed(6)},${pos[1].toFixed(6)}`;
          if (seenFermatePos.has(posKey)) return;
          seenFermatePos.add(posKey);
          elements.push({
            id: `fermata-${k}`,
            type: 'fermata',
            label: `Fermata #${k}`,
            position: pos,
            score: f['punteggio fermata'] ?? 7,
            details: f,
            isExtraurban: isRecordExtraurban(f),
          });
        }
      });
    } else {
      for (let i = 0; i < 16; i++) {
        const angle = (i / 16) * Math.PI * 2;
        const r = 0.008 + ((i * 7) % 5) * 0.004;
        elements.push({ id: `fermata-${i + 1}`, type: 'fermata', label: `Fermata #${i + 1}`, position: [lat + Math.cos(angle) * r, lng + Math.sin(angle) * r], score: 5.5 + ((i * 11) % 50) / 10, details: {} });
      }
    }

    // Sales points
    const detailPV = getCategoryDettaglio(cityData, 'punti_vendita');
    const pvKeys = Object.keys(detailPV);
    if (pvKeys.length > 0) {
      const seenPVPos = new Set();
      pvKeys.forEach(k => {
        const pv = detailPV[k];
        let pos = null;
        if (pv['posizione']) {
          const [pLat, pLng] = pv['posizione'].split(',');
          if (pLat && pLng) pos = [parseFloat(pLat), parseFloat(pLng)];
        }
        if (pos) {
          // Deduplicate by coordinates to avoid same sales point appearing from both urban and extraurban sources
          const posKey = `${pos[0].toFixed(6)},${pos[1].toFixed(6)}`;
          if (seenPVPos.has(posKey)) return;
          seenPVPos.add(posKey);
          elements.push({
            id: `pv-${k}`,
            type: 'punto_vendita',
            label: k,
            position: pos,
            score: pv['punteggio punto vendita'] ?? 7,
            details: pv,
            isExtraurban: isRecordExtraurban(pv),
          });
        }
      });
    } else {
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2 + 0.4;
        const r = 0.012 + i * 0.003;
        elements.push({ id: `pv-${i + 1}`, type: 'punto_vendita', label: `Punto Vendita #${i + 1}`, position: [lat + Math.cos(angle) * r, lng + Math.sin(angle) * r], score: 6.5 + ((i * 13) % 35) / 10, details: {} });
      }
    }

    return elements;
  }, [center, cityData, gtfsLines, cityName]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleDownloadReport = useCallback(async (format) => {
    if (isDownloading || !company) return;
    if (format === 'pptx' && !pptxAvailable) return;
    setIsDownloading(true);
    setDownloadMenuOpen(false);
    setDownloadSubmenuOpen(false);
    try {
      if (format === 'pptx') {
        await downloadClientPptx(company.azienda, selectedCity, company.anno);
      } else {
        await generateReport(company, selectedCity);
      }
    } catch {
      // Download errors surface via the UI state reset below.
    } finally {
      setIsDownloading(false);
    }
  }, [company, selectedCity, isDownloading, pptxAvailable]);

  /**
   * @param {object} el
   * @param {L.LatLng} [popupAt] - where the popup will open (polyline clicks open at the click point)
   */
  const handleElementClick = (el, popupAt) => {
    setSelectedElement(el);
    setSelectedMacroPanel(null);
    // Only show the info popup + center the map — do NOT open sidebar/chart panels.
    // Focus the point that carries the popup; MapViewController then centres the card.
    const clicked = popupAt ? [popupAt.lat, popupAt.lng] : null;
    const target = clicked || (el.type === 'linea' ? getLineLabelPosition(el) : el.position);
    if (target) {
      setElementFocus((prev) => ({
        city: selectedCity || '',
        center: target,
        zoom: 15,
        key: (prev?.key ?? 0) + 1,
      }));
    }
  };


  // ── Derived ───────────────────────────────────────────────────────────────
  const handleOverviewCityClick = (city) => {
    onCitySelect?.(city);
  };

  // Show spatial elements as soon as we are in city mode
  const baseVisibleElements = inCityMode ? mapElements : [];

  // Map legend item id → predicate that returns true when an element matches that filter
  const legendFilterPredicate = (el) => {
    if (el.type === 'linea') {
      const isExtra = el.details?.isExtraurban;
      return isExtra ? activeFilters.has('linee_extra') : activeFilters.has('linee_urban');
    }
    if (el.type === 'fermata') {
      const isExtra = el.isExtraurban ?? isRecordExtraurban(el.details);
      return isExtra ? activeFilters.has('fermate_extra') : activeFilters.has('fermate_urban');
    }
    if (el.type === 'punto_vendita') {
      const isExtra = el.isExtraurban ?? isRecordExtraurban(el.details);
      return isExtra ? activeFilters.has('pv_extra') : activeFilters.has('pv_urban');
    }
    return true;
  };

  const visibleElements = baseVisibleElements.filter(legendFilterPredicate);

  // Legend filter handlers
  const handleToggleFilter = (id) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const handleShowAll = () => setActiveFilters(new Set(['linee_urban', 'linee_extra', 'fermate_urban', 'fermate_extra', 'pv_urban', 'pv_extra']));
  const handleHideAll = () => setActiveFilters(new Set());

  const nonMappableMacroOpen = selectedMacroPanel
    ? !MACRO_BUTTONS.find((b) => b.id === selectedMacroPanel)?.mapType
    : false;



  // Search filtering
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return mapElements.filter((el) => el.label.toLowerCase().includes(q)).slice(0, 8);
  }, [searchQuery, mapElements]);

  return (
    <div className={styles.root}>
      {/* ── FULLSCREEN LEAFLET MAP ── */}
      <MapContainer
        center={center} zoom={10}
        scrollWheelZoom zoomControl={false} attributionControl={false}
        className={styles.map} >
        <TileLayer url={`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png?key=${process.env.REACT_APP_CARTO_KEY}`} subdomains="abcd" opacity={0.8} />
        <MapFitBounds
          positions={fitPositions}
          zoom={inCityMode ? 10 : 9}
          padFactor={inCityMode ? 0.1 : 0.75}
          enabled={!inCityMode}
        />
        <MapViewController
          center={viewCenter}
          zoom={viewZoom}
          focusKey={viewKey}
          animate={false}
          viewEnabled={inCityMode}
        />
        <MapPopupCloser closeSignal={popupCloseSignal} />

        {/* Multi-city overview — all configured cities visible at start */}
        {!inCityMode && configuredCities.map((c) => {
          const score = getCityScore(cittaObj, c);
          const pos = lookupCityCoords(c);
          return (
            <span key={c}>
              <CityZoneLayer
                cityName={c}
                companyName={userCompany}
                revealed={false}
                onZoneClick={() => handleOverviewCityClick(c)}
              />
              <CityInfoMarker
                center={pos}
                cityName={c}
                score={score}
                onClick={() => handleOverviewCityClick(c)}
              />
            </span>
          );
        })}

        {/* Single city or selected city detail view — map content shown immediately */}
        {inCityMode && (
          <span key={zoneCityName}>
            <CityZoneLayer
              cityName={zoneCityName}
              companyName={userCompany}
              revealed
              onLoadingChange={setIsZoneLoading}
            />
          </span>
        )}

        {/* Lines */}
        {visibleElements.filter((el) => el.type === 'linea').map((el) => {
          const isSelected = selectedElement?.id === el.id;
          const routeNum = el.label.replace("Linea ", "").trim();
          const displayRouteNum = `${el.details?.isExtraurban ? 'E' : 'U'}-${routeNum}`;
          const isLineeMacro = activeMacro === 'linee_urbane' || activeMacro === 'linee_extraurbane';

          const color = el.routeColor ?? getLineColor(routeNum, el.details?.isExtraurban);

          const mid = getLineLabelPosition(el);

          const isDetailedLabel = isLineeMacro;
          const pillH = isDetailedLabel ? 42 : 36;
          const imgSzL = isDetailedLabel ? 20 : 18;
          const padV = (pillH - imgSzL) / 2;
          const sColor = scoreColor(el.score);
          const scoreDotHtml = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${sColor};margin-right:3px;"></span>`;
          const busSvg = renderToString(<FaBusAlt style={{ width: imgSzL, height: imgSzL, color: '#ffffff', display: 'block', pointerEvents: 'none', flexShrink: 0 }} />);

          const lineColor = isSelected ? BRAND_VIOLET : color;

          const labelHtml = isDetailedLabel
            ? `<div class="map-line-label" style="width:max-content;display:flex;align-items:center;gap:5px;background:${lineColor};color:#fff;font-weight:900;font-size:13px;font-family:monospace;padding:${padV}px 13px;border-radius:9999px;border:2px solid rgba(255,255,255,0.35);box-shadow:0 2px 10px ${lineColor}88;white-space:nowrap;text-shadow:0 1px 3px rgba(0,0,0,0.6);cursor:pointer;">${busSvg}${displayRouteNum}<span style="display:flex;align-items:center;margin-left:4px;">${scoreDotHtml}<span style="color:${sColor};">${el.score.toFixed(1)}</span></span></div>`
            : `<div class="map-line-label" style="width:max-content;display:flex;align-items:center;gap:4px;background:${lineColor};color:#fff;font-weight:900;font-size:12px;font-family:monospace;padding:${padV}px 11px;border-radius:9999px;box-shadow:0 1px 5px rgba(0,0,0,0.55);white-space:nowrap;text-shadow:0 1px 2px rgba(0,0,0,0.5);cursor:pointer;">${busSvg}${displayRouteNum}</div>`;

          const labelIcon = L.divIcon({
            html: labelHtml,
            className: "",
            iconAnchor: isDetailedLabel ? [36, pillH / 2] : [20, pillH / 2],
          });
          return (
            <span key={el.id}>
              {el.hasGtfs !== false && (
                <Polyline
                  positions={el.multiPath || el.path}
                  pathOptions={{
                    color: lineColor,
                    weight: isSelected ? 7 : 4,
                    opacity: isSelected ? 1 : 0.9,
                    className: "map-polyline-hover"
                  }}
                  eventHandlers={{
                    click: (e) => handleElementClick(el, e.latlng)
                  }}
                >
                  <Tooltip className="map-el-label" direction="top" sticky opacity={1}>
                    <LabelLinea routeNum={routeNum} score={el.score} routeColor={isSelected ? BRAND_VIOLET : color} />
                  </Tooltip>
                  <Popup className="mc-info-popup" maxWidth={280} minWidth={200} offset={[0, -4]} autoPan={false}
                    eventHandlers={{ remove: () => setSelectedElement(null) }}>
                    <MarkerInfoCard element={el} />
                  </Popup>
                </Polyline>
              )}
              <Marker
                position={mid}
                icon={labelIcon}
                eventHandlers={{
                  click: () => handleElementClick(el)
                }}
              >
                <Tooltip className="map-el-label" direction="top" opacity={1} offset={[0, -12]}>
                  <LabelLinea routeNum={routeNum} score={el.score} routeColor={isSelected ? BRAND_VIOLET : color} />
                </Tooltip>
                <Popup className="mc-info-popup" maxWidth={280} minWidth={200} offset={[0, -12]} autoPan={false}
                  eventHandlers={{ remove: () => setSelectedElement(null) }}>
                  <MarkerInfoCard element={el} />
                </Popup>
              </Marker>
            </span>
          );
        })}

        {/* Stops & sales points */}
        {visibleElements.filter((el) => el.type !== 'linea').map((el) => {
          const isSel = selectedElement?.id === el.id;
          const selClass = isSel ? ' selected' : '';

          if (el.type === 'fermata') {
            const isExtra = el.isExtraurban ?? isRecordExtraurban(el.details);
            const sz = isSel ? 46 : 36;
            const half = sz / 2;
            const bg = getFermataMarkerColor(isExtra, isSel);
            const stopColor = isSel ? '#ffffff' : (isExtra ? '#3E4C1E' : '#000000');
            const shadow = isSel
              ? `0 2px 12px rgba(0,0,0,0.55),0 0 0 3px ${BRAND_VIOLET}50`
              : "0 2px 8px rgba(0,0,0,0.42)";
            const imgSz = Math.round(sz * 0.56);
            const busStopSvg = renderToString(<GiBusStop style={{ width: imgSz, height: imgSz, color: stopColor, pointerEvents: 'none', display: 'block' }} />);
            const fermataIcon = L.divIcon({
              html: `<div class="map-point-marker${selClass}" style="width:${sz}px;height:${sz}px;background:${bg};border-radius:50%;box-shadow:${shadow};display:flex;align-items:center;justify-content:center;cursor:pointer;">${busStopSvg}</div>`,
              className: "",
              iconSize: [sz, sz],
              iconAnchor: [half, half],
              popupAnchor: [0, -half],
            });
            return (
              <Marker
                key={el.id}
                position={el.position}
                icon={fermataIcon}
                eventHandlers={{
                  click: () => handleElementClick(el)
                }}
              >
                <Tooltip
                  direction="top"
                  offset={[0, -(half + 4)]}
                  className="map-el-label"
                  opacity={1}
                >
                  <LabelFermata label={el.label} score={el.score} />
                </Tooltip>
                <Popup className="mc-info-popup" maxWidth={270} minWidth={200} autoPan={false}
                  eventHandlers={{ remove: () => setSelectedElement(null) }}>
                  <MarkerInfoCard element={el} />
                </Popup>
              </Marker>
            );
          } else {
            // punto_vendita
            const isExtra = el.isExtraurban ?? isRecordExtraurban(el.details);
            const sz = isSel ? 50 : 40;
            const half = sz / 2;
            const bg = getPuntoVenditaMarkerColor(isExtra, isSel);
            const shadow = isSel
              ? `0 2px 12px rgba(0,0,0,0.55),0 0 0 3px ${BRAND_VIOLET}50`
              : "0 3px 10px rgba(0,0,0,0.42)";
            const imgSz = Math.round(sz * 0.55);
            const shopSvg = renderToString(<BsShop style={{ width: imgSz, height: imgSz, color: '#ffffff', pointerEvents: 'none', display: 'block' }} />);
            const pvIcon = L.divIcon({
              html: `<div class="map-point-marker${selClass}" style="width:${sz}px;height:${sz}px;background:${bg};border-radius:50%;box-shadow:${shadow};display:flex;align-items:center;justify-content:center;cursor:pointer;">${shopSvg}</div>`,
              className: "",
              iconSize: [sz, sz],
              iconAnchor: [half, half],
              popupAnchor: [0, -half],
            });
            return (
              <Marker
                key={el.id}
                position={el.position}
                icon={pvIcon}
                eventHandlers={{
                  click: () => handleElementClick(el)
                }}
              >
                <Tooltip
                  direction="top"
                  offset={[0, -(half + 4)]}
                  className="map-el-label"
                  opacity={1}
                >
                  <LabelPuntoVendita label={el.label} score={el.score} />
                </Tooltip>
                <Popup className="mc-info-popup" maxWidth={270} minWidth={200} autoPan={false}
                  eventHandlers={{ remove: () => setSelectedElement(null) }}>
                  <MarkerInfoCard element={el} />
                </Popup>
              </Marker>
            );
          }
        })}

        <MapLegend
          cityData={cityData}
          revealed={mapRevealed}
          activeFilters={activeFilters}
          onToggleFilter={handleToggleFilter}
          onShowAll={handleShowAll}
          onHideAll={handleHideAll}
        />
        <MapZoomControls />
      </MapContainer>

      {/* ── ZONE LOADING OVERLAY ── */}
      {isZoneLoading && inCityMode && (
        <div className={styles.zoneLoadingOverlay}>
          <div className={styles.spinner}></div>
          <div style={{ marginTop: '1rem', color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>
            Caricamento area in corso...
          </div>
        </div>
      )}

      {/* ── NON-SPATIAL CATEGORY OVERLAY ── */}
      {nonMappableMacroOpen && (
        <div className={styles.nonSpatialOverlay}>
          <div className={styles.nonSpatialMsg}>
            <div className={styles.nonSpatialTitle}>CATEGORIA NON GEOLOCALIZZATA</div>
            <div className={styles.nonSpatialSub}>Vedi i dati nel pannello in basso</div>
          </div>
        </div>
      )}



      <div className={styles.topRightControls}>
        {inCityMode && mapRevealed && (
          <div className={styles.searchWrap} ref={searchRef}>
            <button className={styles.searchBtn} onClick={() => setSearchOpen((v) => !v)} aria-label="Cerca elemento">
              <Search size={16} />
            </button>
            <AnimatePresence>
              {searchOpen && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className={styles.searchBox}>
                  <input
                    autoFocus
                    className={styles.searchInput}
                    placeholder="Cerca linea, fermata…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchResults.length > 0 && (
                    <div className={styles.searchDropdown}>
                      {searchResults.map((el) => (
                        <button key={el.id} className={styles.searchResult}
                          onClick={() => { handleElementClick(el); setSearchOpen(false); setSearchQuery(''); }}>
                          <span className={styles.searchResultLabel}>{el.label}</span>
                          <span className={styles.searchResultScore} style={{ color: scoreColor(el.score) }}>
                            {el.score.toFixed(1)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {mapRevealed && (
          <>
            <div className={styles.downloadWrap} ref={downloadMenuRef}>
              <button
                onClick={() => {
                  if (isDownloading) return;
                  setDownloadMenuOpen((v) => !v);
                  setDownloadSubmenuOpen(false);
                }}
                className={`${styles.downloadBtnCircular} ${isDownloading ? styles.downloadBtnCircularLoading : ''}`}
                title="Scarica report"
                disabled={isDownloading}
                aria-expanded={downloadMenuOpen}
                aria-haspopup="menu"
              >
                <Download size={18} color="#ffffff" className={isDownloading ? styles.downloadIconSpin : ''} />
              </button>

              <AnimatePresence>
                {downloadMenuOpen && !isDownloading && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className={styles.downloadMenu}
                    role="menu"
                  >
                    <button
                      type="button"
                      className={`${styles.downloadMenuItem} ${downloadSubmenuOpen ? styles.downloadMenuItemActive : ''}`}
                      onMouseEnter={() => setDownloadSubmenuOpen(true)}
                      onClick={() => setDownloadSubmenuOpen((v) => !v)}
                      role="menuitem"
                      aria-haspopup="menu"
                      aria-expanded={downloadSubmenuOpen}
                    >
                      <span>Report Esteso</span>
                      <ChevronRight size={14} />
                    </button>

                    <AnimatePresence>
                      {downloadSubmenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, x: -4 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -4 }}
                          transition={{ duration: 0.12 }}
                          className={styles.downloadSubmenu}
                          role="menu"
                        >
                          <button
                            type="button"
                            className={styles.downloadSubmenuItem}
                            onClick={() => handleDownloadReport('pdf')}
                            role="menuitem"
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            className={styles.downloadSubmenuItem}
                            onClick={() => handleDownloadReport('pptx')}
                            disabled={!pptxAvailable}
                            title={pptxAvailable ? 'Scarica PowerPoint' : 'PowerPoint non disponibile'}
                            role="menuitem"
                          >
                            PowerPoint (PPTX)
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={onToggleCompare}
              className={`${styles.confrontoBtn} ${compareMode ? styles.confrontoBtnActive : ''}`}
              title={compareMode ? "Esci dal Confronto" : "Compara Scenario"}
            >
              {compareMode ? (
                <Maximize size={16} />
              ) : (
                <img src="/confronto.svg" alt="Confronto" className={styles.confrontoIcon} />
              )}
              <span>{compareMode ? 'Esci' : 'Confronto'}</span>
            </button>
          </>
        )}
      </div>

    </div>
  );
};

export default MapDashboard;
